import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Link } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import FeatherIcon from 'feather-icons-react';
import { Popover, Space, Tooltip } from 'antd';
import { ModalEditList } from './ModalEditList';
import { ImportListModal } from './ImportListModal';
import { DuplicateListModal } from './DuplicateListModal';
import useCustomListTable from './hooks/useCustomListTable';
import { CustomListTableActions } from './CustomListTableActions';
import { Button } from '../../../../components/buttons/buttons';
import { Cards } from '../../../../components/cards/frame/cards-frame';
import { profileTypes } from '../../../../config/constants/userConstants';
import { useDictionaryTranslation } from '../../../../hooks/useDictionaryTranslation';
import { useNotifications } from '../../../../hooks/useNotifications';
import { DataTable } from '../../../../components/dataTable/data-table';

export const CustomListTable = () => {
  const { translate: t } = useDictionaryTranslation();
  const { showConfirmNotification } = useNotifications();
  const isMaster = useSelector(
    (state) => state.auth?.currentUser?.profile?.idProfile === profileTypes.MASTER.id,
  );
  const [editingList, setEditingList] = useState(null);
  const [importingList, setImportingList] = useState(null);
  const [duplicatingList, setDuplicatingList] = useState(null);
  const [detailsListId, setDetailsListId] = useState(null);
  const [isOpenCustomListsModal, setOpenCustomListModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const { deleteList, tableData, reloadData, getTableData, isLoading } = useCustomListTable();

  const openCreateCustomListModal = () => {
    setEditingList(null);
    setOpenCustomListModal(true);
  };

  const openEditCustomListModal = (customList) => {
    setEditingList(customList);
    setOpenCustomListModal(true);
  };

  const closeCustomListsModal = (refreshData = false) => {
    setOpenCustomListModal(false);
    setEditingList(null);

    if (refreshData) {
      reloadData();
    }
  };

  const openImportListModal = (customList) => {
    setImportingList(customList);
    setShowImportModal(true);
  };

  const closeImportListModal = (refreshData = false) => {
    setShowImportModal(false);
    setImportingList(null);

    if (refreshData) {
      reloadData();
    }
  };

  const openDuplicateListModal = (customList) => {
    setDuplicatingList(customList);
    setShowDuplicateModal(true);
  };

  const closeDuplicateListModal = () => {
    setShowDuplicateModal(false);
    setDuplicatingList(null);
  };

  const getDeleteListAction = (customList) => () => {
    showConfirmNotification({
      title: t('areYouSure'),
      content: t('recordWillBeRemoved', { recordDescription: customList.title }),
      onOk: async () => {
        await deleteList(customList._id);
      },
    });
  };

  const getShowExamType = (list) => () => {
    setDetailsListId(list._id);
  };

  const columns = [
    {
      key: 'title',
      title: t('list'),
      dataIndex: 'title',
    },
    {
      key: 'description',
      title: t('description'),
      dataIndex: 'description',
      align: 'center',
    },
    {
      key: 'actions',
      align: 'center',
      title: t('actions'),
      render: (_text, record) => {
        return (
          <Space size="small" style={{ padding: '0px' }}>
            <Popover
              trigger="focus"
              content={
                <CustomListTableActions
                  customListId={record._id}
                  openImportListModal={() => openImportListModal(record)}
                  getShowExamType={getShowExamType(record)}
                  editCustomListItem={() => openEditCustomListModal(record)}
                  deleteItemList={getDeleteListAction(record)}
                  showDuplicate={isMaster}
                  duplicateToAccount={() => openDuplicateListModal(record)}
                />
              }
              placement="bottom"
              overlayInnerStyle={{ padding: '0px' }}
            >
              <Button outlined type="secondary" style={{ border: 'none' }}>
                <FeatherIcon icon="more-horizontal" />
              </Button>
            </Popover>

            <Button outlined type="secondary" style={{ border: 'none', alignItems: 'center' }}>
              <Link to={`/customLists/${record._id}`}>
                <FeatherIcon icon="search" />
              </Link>
            </Button>
          </Space>
        );
      },
    },
  ];

  if (detailsListId) {
    return <Redirect push to={`/customLists/${detailsListId}`} />;
  }

  return (
    <Cards headless>
      <DataTable
        rowKey="_id"
        columns={columns}
        isLoading={isLoading}
        onChange={getTableData}
        tableData={tableData}
        headerData={{
          show: true,
          searchPlaceholder: t('customList'),
          itemsName: t('customLists'),
          actions: (
            <Tooltip title={t('create')}>
              <Button
                type="primary"
                onClick={openCreateCustomListModal}
                htmlType="button"
                className="add-service"
                icon={<PlusOutlined />}
              />
            </Tooltip>
          ),
        }}
      />

      <ModalEditList
        show={isOpenCustomListsModal}
        handleClose={closeCustomListsModal}
        customListId={editingList?._id}
      />

      <ImportListModal show={showImportModal} handleClose={closeImportListModal} customListId={importingList?._id} />

      <DuplicateListModal
        show={showDuplicateModal}
        handleClose={closeDuplicateListModal}
        customListId={duplicatingList?._id}
      />
    </Cards>
  );
};

export default CustomListTable;
